import type { RolloutView } from "@szyos/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { STUDIO_BUILD, api, errorMessage } from "../api";
import { Icon } from "../icons";
import { HoldButton } from "./HoldButton";
import { ErrorNote, useToast } from "./feedback";

/** Nach dem Ausrollen bleibt die Meldung so lange stehen, damit man sie sieht. */
const SHOW_FINISHED_MS = 30 * 60_000;

const isActive = (v: RolloutView | undefined) =>
  v?.last?.state === "requested" || v?.last?.state === "running";

/**
 * Erfolg bleibt nur stehen, bis die neue Version geladen ist (dieses Studio ist der laufende Stand); ein Abbruch
 * bleibt eine Weile, damit man den Grund sieht.
 */
function recentlyFinished(v: RolloutView): boolean {
  const at = v.last?.finishedAt;
  if (!at || Date.now() - new Date(at).getTime() >= SHOW_FINISHED_MS) return false;
  return v.last?.state === "failed" || STUDIO_BUILD !== v.runningBuild;
}

/** Etwas zu zeigen gibt es nur bei neuem Stand, wartenden Aufträgen oder einem frischen Ausrollen. */
function hasNews(v: RolloutView): boolean {
  const newer = v.available && v.mainSha !== null && !v.mainSha.startsWith(v.runningBuild);
  return newer || v.waitingOrders.length > 0 || isActive(v) || recentlyFinished(v);
}

function StateLine({ view, restarting }: { view: RolloutView; restarting: boolean }) {
  const last = view.last;
  if (!last || (!isActive(view) && !recentlyFinished(view))) return null;
  if (restarting) {
    return (
      <p className="rollout-state">
        <Icon name="clock" /> Das Büro startet neu. Die Seite meldet sich gleich wieder.
      </p>
    );
  }
  if (last.state === "done") {
    return (
      <p className="rollout-state rollout-state--ok">
        <Icon name="check" /> Ausgerollt. {last.message} Lade die Seite neu, um die neue Version zu sehen.
      </p>
    );
  }
  if (last.state === "failed") {
    return (
      <p className="rollout-state rollout-state--error" role="alert">
        <Icon name="alert" /> Ausrollen abgebrochen: {last.message}
      </p>
    );
  }
  return (
    <p className="rollout-state">
      <Icon name="clock" /> Wird ausgerollt: {last.message}
    </p>
  );
}

/**
 * Büro ausrollen (24.10.2026): zeigt, was auf main liegt und noch nicht im Büro läuft, und welche Aufträge erst
 * danach ausgeliefert sind. Der Owner hält „Jetzt ausrollen“; ausführen tut der Helfer auf dem Server.
 */
export function RolloutPanel({ isOwner }: { isOwner: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const hintId = useId();
  const [error, setError] = useState<string | null>(null);
  const rollout = useQuery({
    queryKey: ["rollout"],
    queryFn: api.rollout,
    refetchInterval: (q) => (isActive(q.state.data) ? 4000 : 60_000),
    retry: false,
  });
  const start = useMutation({
    mutationFn: (sha: string) => api.requestRollout(sha),
    onSuccess: (view) => {
      setError(null);
      queryClient.setQueryData(["rollout"], view);
      toast("Ausrollen gestartet. Das Büro startet gleich kurz neu.");
    },
    onError: (err) => {
      setError(errorMessage(err));
      toast(errorMessage(err), "error");
      void queryClient.invalidateQueries({ queryKey: ["rollout"] });
    },
  });

  const view = rollout.data;
  if (!view || !hasNews(view)) return null;
  // Während des Neustarts antwortet das Büro nicht; das ist erwartet und kein Fehler.
  const restarting = isActive(view) && rollout.isError;
  const more = view.aheadTotal - view.ahead.length;

  return (
    <section className="panel panel--section rollout-panel" aria-labelledby="rolloutHead">
      <div className="panel-head">
        <h2 id="rolloutHead">
          <Icon name="rocket" /> Neue Version fürs Büro
        </h2>
        <span className="muted" role="status">
          {isActive(view)
            ? "Wird ausgerollt"
            : view.aheadTotal > 0
              ? `${view.aheadTotal} ${view.aheadTotal === 1 ? "Änderung" : "Änderungen"} nicht ausgerollt`
              : view.waitingOrders.length > 0
                ? "Gemergt, noch nicht ausgerollt"
                : ""}
        </span>
      </div>
      <p className="muted">
        Das Büro läuft auf <code>{view.runningBuild}</code>
        {view.mainSha ? (
          <>
            , auf main liegt <code>{view.mainSha.slice(0, 7)}</code>.
          </>
        ) : (
          "."
        )}{" "}
        Sichtbar wird eine Änderung erst nach dem Ausrollen.
      </p>
      {view.ahead.length > 0 && (
        <ul className="rollout-list">
          {view.ahead.map((c) => (
            <li key={c.sha}>
              <code>{c.sha.slice(0, 7)}</code> {c.title}
            </li>
          ))}
          {more > 0 && <li className="muted">und {more} weitere</li>}
        </ul>
      )}
      {view.waitingOrders.length > 0 && (
        <p>
          Erst danach ausgeliefert:{" "}
          {view.waitingOrders.map((o, i) => (
            <span key={o.id}>
              {i > 0 && ", "}
              <Link to="/work-orders/$orderId" params={{ orderId: o.id }} className="link">
                {o.title}
              </Link>
            </span>
          ))}
        </p>
      )}
      <StateLine view={view} restarting={restarting} />
      {!view.available && (
        <p className="muted">
          {view.reason} Ausrollen geht dann nur vom PC aus: <code>bash deploy/buero/deploy.sh</code>
        </p>
      )}
      {view.available && isOwner && !isActive(view) && view.mainSha && (
        <div className="rollout-actions">
          <HoldButton
            disabled={!view.ready || start.isPending}
            describedBy={hintId}
            onConfirm={() => start.mutate(view.mainSha ?? "")}
            onTooShort={() => toast("Zum Ausrollen gedrückt halten.")}
          >
            {start.isPending ? "Wird übergeben …" : "Jetzt ausrollen"}
          </HoldButton>
          <span id={hintId} className="muted">
            {view.ready
              ? "Gedrückt halten. Das Büro baut den Stand von main und startet kurz neu."
              : view.blockers.join(" ")}
          </span>
        </div>
      )}
      <ErrorNote error={error} />
    </section>
  );
}
